import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as Icons from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { theme } from "../theme";
import { useBreakpoint } from "../hooks/useBreakpoint";

// Helper para crear iconos sin problemas de tipo
const Icon = ({ name, ...props }: { name: keyof typeof Icons; [key: string]: any }) => {
  return React.createElement(Icons[name] as any, props);
};

interface SidebarProps {
  user?: any;
  mobileOpen?: boolean;
  onClose?: () => void;
  tieneContable?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ user, mobileOpen = false, onClose, tieneContable = false }) => {
  const [_collapsed, setCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    reportes: false,
    operaciones: false,
    movimiento_insumos: false,
    movimiento_depositos: false,
    movimiento_informes: false,
    liquidaciones: false,
    recepcion_despacho: false,
    herramientas: false,
    integraciones: false,
    configuracion: false,
    configuracion_maestros: false,
    configuracion_infraestructura: false,
    configuracion_parametros: false,
  });
  const location = useLocation();
  const { isMobileOrTablet } = useBreakpoint();
  const collapsed = isMobileOrTablet ? false : _collapsed;
  const sidebarBgStyle = {
    background: `linear-gradient(180deg, ${theme.colors.sidebarGradientFrom} 0%, ${theme.colors.sidebarGradientTo} 100%)`,
  };
  const sidebarHoverBg = 'rgba(255,255,255,0.12)';
  const brandContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: theme.borderRadius.lg,
    padding: '8px 12px',
    border: '1px solid rgba(255,255,255,0.15)',
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isActive = (path: string) => location.pathname === path;

  // Si no está verificado, solo mostrar Alta Usuarios
  if (user?.verificado === 0 || user?.verificado === undefined) {
    return (
      <>
        {isMobileOrTablet && mobileOpen && (
          <div className="sidebar-backdrop" onClick={onClose} />
        )}
        <motion.aside
          animate={{
            width: collapsed ? 80 : 280,
            x: isMobileOrTablet && !mobileOpen ? -300 : 0,
          }}
          transition={{ type: 'tween', duration: 0.25 }}
          className="h-screen p-4 flex flex-col overflow-y-auto"
          style={{
            ...sidebarBgStyle,
            ...(isMobileOrTablet ? { position: 'fixed', top: 0, left: 0, zIndex: 1000 } : {}),
          }}
        >
        <div className="mb-6 flex items-center justify-between">
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={brandContainerStyle}
            >
              <img 
                src="/logo_1.png" 
                alt="Servisoft" 
                style={{ 
                  height: '35px',
                  width: 'auto',
                }} 
              />
              <span style={{ 
                color: theme.colors.secondary, 
                fontWeight: 600, 
                fontSize: '15px',
                letterSpacing: '0.5px'
              }}>
                ALGODÓN
              </span>
            </motion.div>
          )}
          
        {collapsed && (
          <div style={{ ...brandContainerStyle, justifyContent: 'center', padding: '6px', flexDirection: 'column', gap: '4px' }}>
            <img 
              src="/logo_1.png" 
              alt="Servisoft" 
              style={{ 
                height: '30px',
                width: 'auto',
              }} 
            />
            <span style={{ color: theme.colors.secondary, fontSize: '11px', fontWeight: 600 }}>ALGODÓN</span>
          </div>
        )}
          
          <button
            onClick={isMobileOrTablet ? onClose : () => setCollapsed(!_collapsed)}
            style={{
              color: theme.colors.textLight,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              padding: '4px',
            }}
            aria-label={isMobileOrTablet ? "Cerrar menú" : (collapsed ? "Expandir menú" : "Contraer menú")}
            title={isMobileOrTablet ? "Cerrar menú" : (collapsed ? "Expandir menú" : "Contraer menú")}
          >
            {isMobileOrTablet ? <Icon name="FiX" /> : (collapsed ? <Icon name="FiChevronRight" /> : <Icon name="FiChevronLeft" />)}
          </button>
        </div>

        <nav className="flex flex-col gap-1">
          <Link to="/">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="flex items-center gap-3 p-3 rounded-lg cursor-pointer overflow-hidden transition-colors"
              style={{
                backgroundColor: isActive('/') ? theme.colors.sidebarActiveBg : 'transparent',
                color: isActive('/') ? theme.colors.sidebarActiveText : theme.colors.textLight,
              }}
              onMouseEnter={(e) => !isActive('/') && (e.currentTarget.style.backgroundColor = sidebarHoverBg)}
              onMouseLeave={(e) => !isActive('/') && (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <span className="text-lg"><Icon name="FiUsers" /></span>
              {!collapsed && (
                <span 
                  className="font-normal" 
                  style={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    fontSize: '14.5px'
                  }}
                  title="Alta Usuarios"
                >
                  Alta Usuarios
                </span>
              )}
            </motion.div>
          </Link>
        </nav>
        </motion.aside>
      </>
    );
  }

  // Menú base (siempre visible)
  const baseMenuItems = [
    { 
      type: 'dropdown' as const, 
      icon: <Icon name="FiBarChart2" />, 
      label: "Reportes",
      key: 'reportes',
      children: [
        { icon: <Icon name="FiHome" />, label: "Dashboard", path: "/reportes/compras" },
        { icon: <Icon name="FiTrendingUp" />, label: "Ingreso Camiones", path: "/reportes/ingresocamiones" },
        { icon: <Icon name="FiTrendingUp" />, label: "Egreso Camiones", path: "/reportes/egresocamiones" },
        { icon: <Icon name="FiBarChart2" />, label: "Producción", path: "/reportes/produccion" },
        { icon: <Icon name="FiBarChart2" />, label: "Desmote en General", path: "/reportes" },
        { icon: <Icon name="FiDatabase" />, label: "Evolución Stock", path: "/reportes/stock/EvolucionStock" },
      ]
    }
  ];

  // Item de Autorizar Usuario (solo si es admin)
  const autorizarUsuarioItem = user?.tipo_usuario === "D" ? [
    {
      type: 'single' as const,
      icon: <Icon name="FiUsers" />,
      label: "Autorizar Usuarios",
      path: "/autorizacionusuarios"
    }
  ] : [];

  // Si solo_reportes === 1, mostrar solo menú base + autorizar
  if (user?.solo_reportes === 1) {
    const menuItems = [...baseMenuItems, ...autorizarUsuarioItem];
    
    return (
      <>
        {isMobileOrTablet && mobileOpen && (
          <div className="sidebar-backdrop" onClick={onClose} />
        )}
        <motion.aside
          animate={{
            width: collapsed ? 80 : 280,
            x: isMobileOrTablet && !mobileOpen ? -300 : 0,
          }}
          transition={{ type: 'tween', duration: 0.25 }}
          className="h-screen p-4 flex flex-col overflow-y-auto"
          style={{
            ...sidebarBgStyle,
            ...(isMobileOrTablet ? { position: 'fixed', top: 0, left: 0, zIndex: 1000 } : {}),
          }}
        >
        {/* Logo y botón colapsar */}
        <div className="mb-6 flex items-center justify-between">
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2"
            >
            <img 
              src="/logo_1.png" 
              alt="Servisoft" 
              style={{ 
                height: '35px',
                width: 'auto',
              }} 
            />
              <div style={{ color: theme.colors.textLight }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: theme.colors.textLight,
                }}>
                  <span style={{ color: theme.colors.secondary }}>Servisoft</span>
                </div>
                <div style={{ 
                  fontSize: '12px',
                  opacity: 0.8,
                  color: theme.colors.textLight,
                }}>
                  Algodón
                </div>
              </div>
            </motion.div>
          )}
          
          {collapsed && (
            <img 
              src="/logo_2.png" 
              alt="Servisoft" 
              style={{ 
                height: '35px',
                width: 'auto',
                margin: '0 auto',
              }} 
            />
          )}
          
          <button
            onClick={isMobileOrTablet ? onClose : () => setCollapsed(!_collapsed)}
            style={{
              color: theme.colors.textLight,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              padding: '4px',
            }}
            aria-label={isMobileOrTablet ? "Cerrar menú" : (collapsed ? "Expandir menú" : "Contraer menú")}
            title={isMobileOrTablet ? "Cerrar menú" : (collapsed ? "Expandir menú" : "Contraer menú")}
          >
            {isMobileOrTablet ? <Icon name="FiX" /> : (collapsed ? <Icon name="FiChevronRight" /> : <Icon name="FiChevronLeft" />)}
          </button>
        </div>

        <nav className="flex flex-col gap-1">
          {menuItems.map((item, idx) => {
            if (item.type === 'single') {
              const active = isActive(item.path);
              return (
                <Link key={idx} to={item.path}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer overflow-hidden transition-colors"
                    style={{
                      backgroundColor: active ? theme.colors.sidebarActiveBg : 'transparent',
                      color: active ? theme.colors.sidebarActiveText : theme.colors.sidebarText,
                    }}
                    onMouseEnter={(e) => !active && (e.currentTarget.style.backgroundColor = sidebarHoverBg)}
                    onMouseLeave={(e) => !active && (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <span className="text-lg">{item.icon}</span>
                    {!collapsed && (
                      <span 
                        className="font-normal" 
                        style={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          flex: 1,
                          fontSize: '14.5px'
                        }}
                        title={item.label}
                      >
                        {item.label}
                      </span>
                    )}
                  </motion.div>
                </Link>
              );
            }

            if (item.type === 'dropdown') {
              const isExpanded = expandedSections[item.key!];
              return (
                <div key={idx}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    onClick={() => !collapsed && toggleSection(item.key!)}
                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer overflow-hidden transition-colors"
                    style={{
                      color: theme.colors.textLight,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = sidebarHoverBg}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <span className="text-lg">{item.icon}</span>
                    {!collapsed && (
                      <>
                        <span 
                          className="font-normal flex-1" 
                          style={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            fontSize: '14.5px'
                          }}
                          title={item.label}
                        >
                          {item.label}
                        </span>
                        <span className="text-sm" style={{ flexShrink: 0 }}>
                          {isExpanded ? <Icon name="FiChevronUp" /> : <Icon name="FiChevronDown" />}
                        </span>
                      </>
                    )}
                  </motion.div>
                  
                  <AnimatePresence>
                    {!collapsed && isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        {item.children?.map((child, childIdx) => {
                          const childActive = isActive(child.path);
                          return (
                            <Link key={childIdx} to={child.path}>
                              <motion.div
                                whileHover={{ scale: 1.02 }}
                                className="flex items-center gap-3 p-3 ml-6 rounded-lg cursor-pointer overflow-hidden transition-colors"
                                  style={{
                                    backgroundColor: childActive ? theme.colors.sidebarActiveBg : 'transparent',
                                    color: childActive ? theme.colors.sidebarActiveText : theme.colors.sidebarText,
                                  }}
                                onMouseEnter={(e) => !childActive && (e.currentTarget.style.backgroundColor = sidebarHoverBg)}
                                onMouseLeave={(e) => !childActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                              >
                                <span className="text-sm">{child.icon}</span>
                                <span 
                                  className="font-normal text-sm" 
                                  style={{ 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    fontSize: '13.5px'
                                  }}
                                  title={child.label}
                                >
                                  {child.label}
                                </span>
                              </motion.div>
                            </Link>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            return null;
          })}
        </nav>
        </motion.aside>
      </>
    );
  }

  // Menú Operador Desmotadora
  if (user?.tipo_usuario === 'O') {
    const operadorMenuItems = [
      {
        type: 'single' as const,
        icon: <Icon name="FiBarChart2" />,
        label: "Producción Desmote",
        path: "/ListadoProduccion"
      },
    ];

    return (
      <>
        {isMobileOrTablet && mobileOpen && (
          <div className="sidebar-backdrop" onClick={onClose} />
        )}
        <motion.aside
          animate={{
            width: collapsed ? 80 : 280,
            x: isMobileOrTablet && !mobileOpen ? -300 : 0,
          }}
          transition={{ type: 'tween', duration: 0.25 }}
          className="h-screen p-4 flex flex-col overflow-y-auto"
          style={{
            ...sidebarBgStyle,
            ...(isMobileOrTablet ? { position: 'fixed', top: 0, left: 0, zIndex: 1000 } : {}),
          }}
        >
        <div className="mb-6 flex items-center justify-between">
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={brandContainerStyle}
            >
              <img src="/logo_1.png" alt="Servisoft" style={{ height: '35px', width: 'auto' }} />
              <span style={{ color: theme.colors.secondary, fontWeight: 600, fontSize: '15px', letterSpacing: '0.5px' }}>
                ALGODÓN
              </span>
            </motion.div>
          )}
          {collapsed && (
            <div style={{ ...brandContainerStyle, justifyContent: 'center', padding: '6px', flexDirection: 'column', gap: '4px' }}>
              <img src="/logo_1.png" alt="Servisoft" style={{ height: '30px', width: 'auto' }} />
              <span style={{ color: theme.colors.secondary, fontSize: '11px', fontWeight: 600 }}>ALGODÓN</span>
            </div>
          )}
          <button
            onClick={isMobileOrTablet ? onClose : () => setCollapsed(!_collapsed)}
            style={{ color: theme.colors.textLight, background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '4px' }}
            aria-label={isMobileOrTablet ? "Cerrar menú" : (collapsed ? "Expandir menú" : "Contraer menú")}
          >
            {isMobileOrTablet ? <Icon name="FiX" /> : (collapsed ? <Icon name="FiChevronRight" /> : <Icon name="FiChevronLeft" />)}
          </button>
        </div>

        <nav className="flex flex-col gap-1">
          {operadorMenuItems.map((item, idx) => {
            const active = isActive(item.path);
            return (
              <Link key={idx} to={item.path}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center gap-3 p-3 rounded-lg cursor-pointer overflow-hidden transition-colors"
                  style={{
                    backgroundColor: active ? theme.colors.sidebarActiveBg : 'transparent',
                    color: active ? theme.colors.sidebarActiveText : theme.colors.textLight,
                  }}
                  onMouseEnter={(e) => !active && (e.currentTarget.style.backgroundColor = sidebarHoverBg)}
                  onMouseLeave={(e) => !active && (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <span className="text-lg">{item.icon}</span>
                  {!collapsed && (
                    <span
                      className="font-normal"
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, fontSize: '14.5px' }}
                      title={item.label}
                    >
                      {item.label}
                    </span>
                  )}
                </motion.div>
              </Link>
            );
          })}
        </nav>
        </motion.aside>
      </>
    );
  }

  // Menú completo
  const fullMenuItems = [
    ...baseMenuItems,
    // Operaciones - Flujo productivo principal
    {
      type: 'dropdown' as const,
      icon: <Icon name="FiTool" />,
      label: "Operaciones",
      key: 'operaciones',
      children: [
        { icon: <Icon name="FiShoppingCart" />, label: "Balanza de Camiones", path: "/listar_pesaje" },
        { icon: <Icon name="FiBarChart2" />, label: "Producción Desmote", path: "/ListadoProduccion" },
        { icon: <Icon name="FiLayers" />, label: "Clasificación", path: "/listado_clasificacion" },
        { icon: <Icon name="FiFileText" />, label: "Contratos", path: "/listadocontratos" },
        { icon: <Icon name="FiFileText" />, label: "Órdenes", path: "/listadoordenes" },
        { icon: <Icon name="FiTruck" />, label: "Recepción/Despacho", path: "/listado_recepcion_despacho" },
      ]
    },
    {
      type: 'dropdown' as const,
      icon: <Icon name="FiBox" />,
      label: "Movimiento Insumos",
      key: 'movimiento_insumos',
      children: [
        {
          type: 'subdropdown' as const,
          label: "Depósitos",
          key: 'movimiento_depositos',
          children: [
            { icon: <Icon name="FiShoppingCart" />, label: "Órdenes de Compra", path: "/listar_ocompra" },
            { icon: <Icon name="FiTrendingUp" />, label: "Ingresos", path: "/listar_ingresos" },
            { icon: <Icon name="FiTrendingUp" />, label: "Egresos", path: "/listar_egresos" },
            {
              type: 'subdropdown' as const,
              label: "Informes",
              key: 'movimiento_informes',
              children: [
                { icon: <Icon name="FiFileText" />, label: "Pendientes de Ingreso", path: "/listar_pendiente_ingreso" },
                { icon: <Icon name="FiFileText" />, label: "Pendientes de Facturación", path: "/listar_pendiente_facturar" },
                { icon: <Icon name="FiFileText" />, label: "Pendientes de Órdenes", path: "/listar_pendiente_ordenes" },
                { icon: <Icon name="FiBarChart2" />, label: "Diario Stock", path: "/diario_movimientos" },
                { icon: <Icon name="FiDatabase" />, label: "Inventario", path: "/inventario" },
                { icon: <Icon name="FiTrendingUp" />, label: "Consumo Interno", path: "/consumo_interno" },
              ]
            }
          ]
        }
      ]
    },
    {
      type: 'dropdown' as const,
      icon: <Icon name="FiDollarSign" />,
      label: "Liquidaciones",
      key: 'liquidaciones',
      children: [
        { icon: <Icon name="FiTruck" />, label: "Fletes", path: "/listar_liquidaciones_fletes" },
        { icon: <Icon name="FiShoppingCart" />, label: "Liquidación Compra Algodón", path: "/listar_compraalgodon" },
        { icon: <Icon name="FiSettings" />, label: "Liquidación Servicios Desmote", path: "/listar_servicio_desmote" },
      ]
    },
    // Configuración
    {
      type: 'dropdown' as const,
      icon: <Icon name="FiSettings" />,
      label: "Configuración",
      key: 'configuracion',
      children: [
        {
          type: 'subdropdown' as const,
          label: "Maestros",
          key: 'configuracion_maestros',
          children: [
            { icon: <Icon name="FiUsers" />, label: "Padrón", path: "/listado_padron" },
            { icon: <Icon name="FiMapPin" />, label: "Establecimientos", path: "/listar_productor_establecimiento_lote" },
            { icon: <Icon name="FiTruck" />, label: "Transporte", path: "/transporte" },
          ]
        },
        {
          type: 'subdropdown' as const,
          label: "Infraestructura",
          key: 'configuracion_infraestructura',
          children: [
            { icon: <Icon name="FiGrid" />, label: "Fábrica", path: "/fabrica" },
            { icon: <Icon name="FiPackage" />, label: "Insumos", path: "/insumos" },
          ]
        },
        {
          type: 'subdropdown' as const,
          label: "Parámetros",
          key: 'configuracion_parametros',
          children: [
            { icon: <Icon name="FiPackage" />, label: "Producción", path: "/configuracion_produccion" },
            { icon: <Icon name="FiGrid" />, label: "Celdas", path: "/listado_celdas" },
            { icon: <Icon name="FiFileText" />, label: "Retenciones / Percepciones", path: "/liquidacion/retenciones" },
            ...(tieneContable ? [{ icon: <Icon name="FiDollarSign" />, label: "Contable Liquidación", path: "/configuracion/contable" }] : []),
          ]
        },
        // Autorizar Usuarios solo se muestra si el usuario es tipo "D" (admin)
        ...(user?.tipo_usuario === "D" ? [
          { icon: <Icon name="FiUsers" />, label: "Autorizar Usuarios", path: "/autorizacionusuarios" }
        ] : [])
      ]
    },
    // Herramientas (solo administradores - tipo_usuario === 'D')
    ...(user?.tipo_usuario === "D" ? [
      {
        type: 'dropdown' as const,
        icon: <Icon name="FiSliders" />,
        label: "Herramientas",
        key: 'herramientas',
        children: [
          { icon: <Icon name="FiPercent" />, label: "Ajustar Semilla", path: "/herramientas/ajustar-semilla" },
        ]
      }
    ] : []),
    // Integraciones
    {
      type: 'dropdown' as const,
      icon: <Icon name="FiTool" />,
      label: "Integraciones",
      key: 'integraciones',
      children: [
        { icon: <Icon name="FiTool" />, label: "John Deere", path: "/john-deere" },
      ]
    }
  ];

  const renderMenuItem = (item: any, idx: number) => {
    if (item.type === 'single') {
      const active = isActive(item.path);
      return (
        <Link key={idx} to={item.path}>
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex items-center gap-3 p-3 rounded-lg cursor-pointer overflow-hidden transition-colors"
            style={{
              backgroundColor: active ? theme.colors.sidebarActiveBg : 'transparent',
              color: active ? theme.colors.sidebarActiveText : theme.colors.sidebarText,
            }}
            onMouseEnter={(e) => !active && (e.currentTarget.style.backgroundColor = sidebarHoverBg)}
            onMouseLeave={(e) => !active && (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <span className="text-lg">{item.icon}</span>
            {!collapsed && (
              <span 
                className="font-normal" 
                style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  flex: 1,
                  fontSize: '14.5px'
                }}
                title={item.label}
              >
                {item.label}
              </span>
            )}
          </motion.div>
        </Link>
      );
    }

    if (item.type === 'dropdown') {
      const isExpanded = expandedSections[item.key];
      return (
        <div key={idx}>
          <motion.div
            whileHover={{ scale: 1.02 }}
            onClick={() => !collapsed && toggleSection(item.key)}
            className="flex items-center gap-3 p-3 rounded-lg cursor-pointer overflow-hidden transition-colors"
            style={{
              color: theme.colors.textLight,
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = sidebarHoverBg}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span className="text-lg">{item.icon}</span>
            {!collapsed && (
              <>
                <span 
                  className="font-normal flex-1" 
                  style={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    fontSize: '14.5px'
                  }}
                  title={item.label}
                >
                  {item.label}
                </span>
                <span className="text-sm" style={{ flexShrink: 0 }}>
                  {isExpanded ? <Icon name="FiChevronUp" /> : <Icon name="FiChevronDown" />}
                </span>
              </>
            )}
          </motion.div>
          
          <AnimatePresence>
            {!collapsed && isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                {item.children?.map((child: any, childIdx: number) => {
                  if (child.type === 'subdropdown') {
                    const isSubExpanded = expandedSections[child.key];
                    return (
                      <div key={childIdx}>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          onClick={() => toggleSection(child.key)}
                          className="flex items-center gap-3 p-3 ml-6 rounded-lg cursor-pointer overflow-hidden transition-colors"
                          style={{
                            color: theme.colors.textLight,
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = sidebarHoverBg}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <span 
                            className="font-normal flex-1 text-sm" 
                            style={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              fontSize: '13.5px'
                            }}
                            title={child.label}
                          >
                            {child.label}
                          </span>
                          <span className="text-sm" style={{ flexShrink: 0 }}>
                            {isSubExpanded ? <Icon name="FiChevronUp" /> : <Icon name="FiChevronDown" />}
                          </span>
                        </motion.div>
                        
                        <AnimatePresence>
                          {isSubExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              {child.children?.map((subChild: any, subChildIdx: number) => {
                                // Manejar subdropdown de nivel 3
                                if (subChild.type === 'subdropdown') {
                                  const isSubSubExpanded = expandedSections[subChild.key];
                                  return (
                                    <div key={subChildIdx}>
                                      <motion.div
                                        whileHover={{ scale: 1.02 }}
                                        onClick={() => toggleSection(subChild.key)}
                                        className="flex items-center gap-3 p-3 ml-12 rounded-lg cursor-pointer overflow-hidden transition-colors"
                                        style={{ color: theme.colors.textLight }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = sidebarHoverBg}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                      >
                                        <span 
                                          className="font-normal flex-1 text-sm" 
                                          style={{ 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis', 
                                            whiteSpace: 'nowrap',
                                            fontSize: '13.5px'
                                          }}
                                          title={subChild.label}
                                        >
                                          {subChild.label}
                                        </span>
                                        <span className="text-sm" style={{ flexShrink: 0 }}>
                                          {isSubSubExpanded ? <Icon name="FiChevronUp" /> : <Icon name="FiChevronDown" />}
                                        </span>
                                      </motion.div>
                                      
                                      <AnimatePresence>
                                        {isSubSubExpanded && (
                                          <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                          >
                                            {subChild.children?.map((subSubChild: any, subSubChildIdx: number) => {
                                              const subSubChildActive = isActive(subSubChild.path);
                                              return (
                                                <Link key={subSubChildIdx} to={subSubChild.path}>
                                                  <motion.div
                                                    whileHover={{ scale: 1.02 }}
                                                    className="flex items-center gap-3 p-3 ml-16 rounded-lg cursor-pointer overflow-hidden transition-colors"
                                                      style={{
                                                        backgroundColor: subSubChildActive ? theme.colors.sidebarActiveBg : 'transparent',
                                                        color: subSubChildActive ? theme.colors.sidebarActiveText : theme.colors.sidebarText,
                                                      }}
                                                    onMouseEnter={(e) => !subSubChildActive && (e.currentTarget.style.backgroundColor = sidebarHoverBg)}
                                                    onMouseLeave={(e) => !subSubChildActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                                                  >
                                                    <span className="text-xs">{subSubChild.icon}</span>
                                                    <span 
                                                      className="font-normal text-xs" 
                                                      style={{ 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        flex: 1,
                                                        fontSize: '12.5px'
                                                      }}
                                                      title={subSubChild.label}
                                                    >
                                                      {subSubChild.label}
                                                    </span>
                                                  </motion.div>
                                                </Link>
                                              );
                                            })}
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  );
                                }
                                
                                // Items normales de nivel 2
                                const subChildActive = isActive(subChild.path);
                                return (
                                  <Link key={subChildIdx} to={subChild.path}>
                                    <motion.div
                                      whileHover={{ scale: 1.02 }}
                                      className="flex items-center gap-3 p-3 ml-12 rounded-lg cursor-pointer overflow-hidden transition-colors"
                                        style={{
                                          backgroundColor: subChildActive ? theme.colors.sidebarActiveBg : 'transparent',
                                          color: subChildActive ? theme.colors.sidebarActiveText : theme.colors.sidebarText,
                                        }}
                                      onMouseEnter={(e) => !subChildActive && (e.currentTarget.style.backgroundColor = sidebarHoverBg)}
                                      onMouseLeave={(e) => !subChildActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                      <span className="text-sm">{subChild.icon}</span>
                                      <span 
                                        className="font-normal text-sm" 
                                        style={{ 
                                          overflow: 'hidden', 
                                          textOverflow: 'ellipsis', 
                                          whiteSpace: 'nowrap',
                                          flex: 1,
                                          fontSize: '13.5px'
                                        }}
                                        title={subChild.label}
                                      >
                                        {subChild.label}
                                      </span>
                                    </motion.div>
                                  </Link>
                                );
                              })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  }

                  const childActive = isActive(child.path);
                  return (
                    <Link key={childIdx} to={child.path}>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="flex items-center gap-3 p-3 ml-6 rounded-lg cursor-pointer overflow-hidden transition-colors"
                          style={{
                            backgroundColor: childActive ? theme.colors.sidebarActiveBg : 'transparent',
                            color: childActive ? theme.colors.sidebarActiveText : theme.colors.sidebarText,
                          }}
                        onMouseEnter={(e) => !childActive && (e.currentTarget.style.backgroundColor = sidebarHoverBg)}
                        onMouseLeave={(e) => !childActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <span className="text-sm">{child.icon}</span>
                        <span 
                          className="font-normal text-sm" 
                          style={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            flex: 1,
                            fontSize: '13.5px'
                          }}
                          title={child.label}
                        >
                          {child.label}
                        </span>
                      </motion.div>
                    </Link>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {isMobileOrTablet && mobileOpen && (
        <div className="sidebar-backdrop" onClick={onClose} />
      )}
      <motion.aside
        animate={{
          width: collapsed ? 80 : 280,
          x: isMobileOrTablet && !mobileOpen ? -300 : 0,
        }}
        transition={{ type: 'tween', duration: 0.25 }}
        className="h-screen p-4 flex flex-col overflow-y-auto"
        style={{
          ...sidebarBgStyle,
          ...(isMobileOrTablet ? { position: 'fixed', top: 0, left: 0, zIndex: 1000 } : {}),
        }}
      >
      {/* Logo y botón colapsar */}
      <div className="mb-6 flex items-center justify-between">
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={brandContainerStyle}
          >
            <img 
              src="/logo_1.png" 
              alt="Servisoft" 
              style={{ 
                height: '35px',
                width: 'auto',
              }} 
            />
            <div style={{ color: theme.colors.textLight }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 600,
                lineHeight: 1.2,
                color: theme.colors.textLight,
              }}>
                <span style={{ color: theme.colors.secondary }}>Servisoft</span>
              </div>
              <div style={{ 
                fontSize: '12px',
                opacity: 0.8,
                color: theme.colors.textLight,
              }}>
                Algodón
              </div>
            </div>
          </motion.div>
        )}
        
        {collapsed && (
          <div style={{ ...brandContainerStyle, justifyContent: 'center', padding: '6px' }}>
            <img 
              src="/logo_1.png" 
              alt="Servisoft" 
              style={{ 
                height: '30px',
                width: 'auto',
              }} 
            />
          </div>
        )}
        
        <button
          onClick={isMobileOrTablet ? onClose : () => setCollapsed(!_collapsed)}
          style={{
            color: theme.colors.textLight,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '20px',
            padding: '4px',
          }}
          aria-label={isMobileOrTablet ? "Cerrar menú" : (collapsed ? "Expandir menú" : "Contraer menú")}
        >
          {isMobileOrTablet ? <Icon name="FiX" /> : (collapsed ? <Icon name="FiChevronRight" /> : <Icon name="FiChevronLeft" />)}
        </button>
      </div>

      <nav className="flex flex-col gap-1">
        {fullMenuItems.map((item, idx) => renderMenuItem(item, idx))}
      </nav>
      </motion.aside>
    </>
  );
};

